import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message, Button } from "antd";
import { CloseCircleOutlined, HomeOutlined, ReloadOutlined } from "@ant-design/icons";

/**
 * Professional error page component for OAuth authentication failures.
 * Displays user-friendly error messages and provides options to retry or return to login.
 * 
 * Handles error parameters from OAuth callback redirects and provides
 * appropriate user feedback with clear next steps.
 */
const AuthError = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState("");
  const [isRetrying, setIsRetrying] = useState(false);

  /**
   * Extract and process error information from URL parameters.
   * Sets appropriate error message and displays user notification.
   */
  useEffect(() => {
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");
    
    if (error) {
      // Decode and set the primary error message
      let decodedError = decodeURIComponent(error);
      
      // Add additional context if error description is available
      if (errorDescription) {
        const decodedDescription = decodeURIComponent(errorDescription);
        decodedError = `${decodedError}: ${decodedDescription}`;
      }
      
      setErrorMessage(decodedError);
      message.error("Authentication failed. Please try again.");
    } else {
      setErrorMessage("An unknown authentication error occurred.");
    }
  }, [searchParams]);

  /**
   * Navigate back to the login page
   */
  const handleReturnToLogin = () => {
    navigate("/");
  };

  /**
   * Attempt to retry the authentication process
   */
  const handleRetryAuth = () => {
    setIsRetrying(true);
    // Clear any existing session data that might cause conflicts
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("staff_info");
    sessionStorage.removeItem("customerId");
    sessionStorage.removeItem("token_expires_at");
    
    // Small delay for better UX, then redirect to login
    setTimeout(() => {
      setIsRetrying(false);
      navigate("/");
    }, 1000);
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#f8f9fa',
      padding: '20px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",
    }}>
      {/* Error Icon */}
      <CloseCircleOutlined 
        style={{ 
          fontSize: '4rem', 
          color: '#ff4d4f', 
          marginBottom: '1.5rem',
          filter: 'drop-shadow(0 2px 4px rgba(255, 77, 79, 0.2))'
        }} 
      />
      
      {/* Error Title */}
      <h1 style={{ 
        color: '#ff4d4f', 
        marginBottom: '1rem', 
        textAlign: 'center',
        fontSize: '2rem',
        fontWeight: '600',
        margin: '0 0 1rem 0'
      }}>
        Authentication Failed
      </h1>
      
      {/* Error Details Card */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        border: 'none'
      }}>
        {/* Error Message */}
        <div style={{
          backgroundColor: '#fff2f0',
          border: '1px solid #ffccc7',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <p style={{ 
            color: '#a8071a', 
            margin: '0',
            fontSize: '1rem',
            lineHeight: '1.5',
            fontWeight: '500'
          }}>
            {errorMessage || "We encountered an issue while trying to authenticate you. Please try again."}
          </p>
        </div>
        
        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <Button 
            type="primary" 
            icon={<ReloadOutlined />}
            onClick={handleRetryAuth}
            size="large"
            loading={isRetrying}
            style={{
              minWidth: '140px',
              height: '44px',
              borderRadius: '8px',
              fontWeight: '500'
            }}
          >
            {isRetrying ? 'Retrying...' : 'Try Again'}
          </Button>
          
          <Button 
            icon={<HomeOutlined />}
            onClick={handleReturnToLogin}
            size="large"
            style={{
              minWidth: '140px',
              height: '44px',
              borderRadius: '8px',
              fontWeight: '500'
            }}
          >
            Back to Login
          </Button>
        </div>
      </div>
      
      {/* Support Information */}
      <div style={{
        marginTop: '2rem',
        textAlign: 'center',
        maxWidth: '400px'
      }}>
        <p style={{ 
          color: '#8c8c8c', 
          fontSize: '0.9rem',
          lineHeight: '1.5',
          margin: '0'
        }}>
          If this problem persists, please contact your system administrator or support team for assistance.
        </p>
      </div>
    </div>
  );
};

export default AuthError; 